import { cn, Tour, useDocumentTitle } from "@quortex/front-common"
import { useNavigate, useSearch } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import { ReactFlowProvider } from "@xyflow/react"
import { useEffect, useMemo } from "react"
import { ActiveViewersCard } from "@/components/ActiveViewersCard"
import { EstimatedUsageCard } from "@/components/EstimatedUsageCard"
import { useWorkspacePath } from "@/hooks/navigation"
import { EmptySegmentation } from "@/pages/ContentSteering/Segmentation/EmptySegmentation"
import { SegmentationFlow } from "@/pages/ContentSteering/Segmentation/SegmentationFlow"
import { QoEStatusCard } from "@/pages/ContentSteering/Segmentation/SegmentationPage/QoEStatusCard"
import { TopGeoSegmentsCard } from "@/pages/ContentSteering/Segmentation/SegmentationPage/TopGeoSegmentsCard"
import type { AddSegmentModalGenerics } from "@/pages/ContentSteering/Segmentation/SegmentEdition/AddSegmentModal"
import { useSegmentList } from "@/queries/segments"
import { useOverallStatistics } from "@/queries/statistics"
import { useWorkspaceList } from "@/queries/workspaces"
import { useSegmentationFallbackTour } from "@/stores/segmentationFallbackTour"
import { useSegmentationRootTour } from "@/stores/segmentationRootTour"

const SegmentationPage = () => {
  const { data: segments } = useSuspenseQuery({ ...useSegmentList() })
  const { data: workspace } = useSuspenseQuery({ ...useWorkspaceList(), select: d => d.results[0] })
  const { data: statistics, dataUpdatedAt } = useSuspenseQuery({
    ...useOverallStatistics(),
    refetchInterval: 10_000
  })
  const isCapacityBalancingEnabled = useMemo(
    () => workspace?.capacityBalancingEnabled,
    [workspace?.capacityBalancingEnabled]
  )

  const search = useSearch<AddSegmentModalGenerics>()
  const storedSearchView = localStorage.getItem("search.view")

  useDocumentTitle(`Segmentation - ${workspace?.name}`)
  const navigate = useNavigate()
  const segmentationRootTour = useSegmentationRootTour(state => state)
  const segmentationFallbackTour = useSegmentationFallbackTour(state => state)
  const workspacePath = useWorkspacePath()

  useEffect(() => {
    // Store the search view in localStorage to persist it across page navigations
    if (search.view !== undefined) localStorage.setItem("search.view", search.view || "")
  }, [search.view])

  useEffect(() => {
    // On first load, if there's a stored view preference, apply it
    if (
      storedSearchView &&
      storedSearchView === "segmentationExpanded" &&
      search.view === undefined &&
      !search.modal
    ) {
      navigate({
        replace: true,
        search: {
          view: "segmentationExpanded"
        }
      })
    }
  }, [storedSearchView, search.view, search.modal, navigate])

  //  If there are no segments, ensure we are not in expanded view
  if (segments?.length === 0) {
    localStorage.removeItem("search.view")
    if (search.view === "segmentationExpanded" && !search.modal) {
      navigate({
        replace: true,
        search: {
          view: undefined
        },
        to: `${workspacePath}/segmentation`
      })
    }
    return (
      <div>
        <EmptySegmentation />
      </div>
    )
  }

  return (
    <>
      {search.view !== "segmentationExpanded" && (
        <>
          <h2 className="typo-2xl-medium mb-lg">Segmentation</h2>

          <div
            className={cn(
              "mb-lg grid gap-sm",
              isCapacityBalancingEnabled ? "grid-cols-4" : "grid-cols-3"
            )}
          >
            <ActiveViewersCard
              refetchedAt={new Date(dataUpdatedAt)}
              viewers={statistics.overallViewers}
            />
            {isCapacityBalancingEnabled && (
              <EstimatedUsageCard
                estimatedUsage={statistics.overallEstimatedUsedBandwidth}
                refetchedAt={new Date(dataUpdatedAt)}
              />
            )}
            <TopGeoSegmentsCard viewersPerSegment={statistics.viewersPerSegment} />
            <QoEStatusCard overallQoe={statistics.overallQoe} />
          </div>
        </>
      )}
      <div
        className={cn(
          "h-full min-h-56 w-full grow bg-grey-100",
          search.view !== "segmentationExpanded" && "radius border-2 border-grey-300"
        )}
      >
        <ReactFlowProvider>
          <SegmentationFlow segments={segments} />
          <Tour {...segmentationRootTour} />
          <Tour {...segmentationFallbackTour} />
        </ReactFlowProvider>
      </div>
    </>
  )
}

export default SegmentationPage
